import { MUI_MENU_ITEM, MUI_TAB, MUI_TABLE, MUI_TOOLTIP, THEME_PRIMARY_MAIN } from "@constants/mui.constants";
import { brandPrimaryHover, buttonBorderRadius } from "@config/theme/tokens";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type { ReactNode } from "react";

const theme = createTheme({
	palette: {
		primary: {
			main: THEME_PRIMARY_MAIN,
		},
	},
	typography: {
		fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		// Set font family for all typography variants
		h1: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h2: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h3: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h4: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h5: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h6: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		body1: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		body2: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		button: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		caption: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		overline: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				'*': {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					"&:hover": {
						backgroundColor: MUI_MENU_ITEM.HOVER.BACKGROUND_COLOR,
						color: MUI_MENU_ITEM.HOVER.COLOR,
					},
					"&.Mui-selected": {
						backgroundColor: MUI_MENU_ITEM.SELECTED.BACKGROUND_COLOR,
						color: MUI_MENU_ITEM.SELECTED.COLOR,
					},
					"&.Mui-selected:hover": {
						backgroundColor: MUI_MENU_ITEM.SELECTED.HOVER_COLOR,
						color: MUI_MENU_ITEM.SELECTED.COLOR,
					},
				},
			},
		},
		MuiSvgIcon: {
			styleOverrides: {
				root: {},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					background: MUI_TOOLTIP.TOOLTIP_BACKGROUND_COLOR,
					color: MUI_TOOLTIP.TOOLTIP_COLOR,
					boxShadow: MUI_TOOLTIP.TOOLTIP_BOX_SHADOW,
					borderRadius: MUI_TOOLTIP.TOOLTIP_BORDER_RADIUS,
					fontSize: MUI_TOOLTIP.TOOLTIP_FONT_SIZE,
					padding: MUI_TOOLTIP.TOOLTIP_PADDING,
					letterSpacing: MUI_TOOLTIP.TOOLTIP_LETTER_SPACING,
				},
				arrow: {
					color: MUI_TOOLTIP.TOOLTIP_BACKGROUND_COLOR,
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					"& .MuiTabs-indicator": {
						backgroundColor: MUI_TAB.SELECTED.INDICATOR_COLOR,
						height: 4,
						borderRadius: 2,
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					textTransform: "capitalize",
					fontSize: 12,
					borderRadius: 8,
					minHeight: 40,
					marginRight: 8,
					transition: "background 0.2s, color 0.2s",
					"&.Mui-selected": {
						color: MUI_TAB.SELECTED.TAB_NAME_COLOR,
						backgroundColor: MUI_TAB.SELECTED.BACKGROUND_COLOR,
					},
					"&.Mui-selected:hover": {
						backgroundColor: MUI_TAB.SELECTED.HOVER_COLOR,
					},
					"&:hover": {
						backgroundColor: MUI_TAB.SELECTED.HOVER_COLOR,
					},
				},
			},
		},
		// Add font family to common components
		MuiButton: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					borderRadius: `${buttonBorderRadius}px`,
				},
				containedPrimary: {
					"&:hover": {
						backgroundColor: brandPrimaryHover,
					},
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					"&.Mui-disabled": {
						opacity: 1,
						WebkitTextFillColor: "#111827",
					},
				},
				input: {
					"&.Mui-disabled": {
						opacity: 1,
						WebkitTextFillColor: "#111827",
						color: "#111827",
					},
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiAccordionDetails: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiAlert: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiSkeleton: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiCircularProgress: {
			styleOverrides: {
				root: {
					color: THEME_PRIMARY_MAIN,
				},
			},
		},
		MuiTableContainer: {
			styleOverrides: {
				root: {
					borderRadius: `${MUI_TABLE.WRAPPER_RADIUS}px`,
					// Do not set overflow: hidden — it blocks vertical scroll when maxHeight is used.
					overflowX: "auto",
					border: MUI_TABLE.WRAPPER_BORDER,
					boxShadow: "none",
					backgroundColor: "#ffffff",
				},
			},
		},
		MuiTable: {
			styleOverrides: {
				root: {
					borderCollapse: "separate",
					borderSpacing: 0,
				},
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					"& .MuiTableCell-head:first-of-type": {
						borderTopLeftRadius: `${MUI_TABLE.WRAPPER_RADIUS}px`,
					},
					"& .MuiTableCell-head:last-of-type": {
						borderTopRightRadius: `${MUI_TABLE.WRAPPER_RADIUS}px`,
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				head: {
					backgroundColor: MUI_TABLE.HEADER.BACKGROUND,
					color: MUI_TABLE.HEADER.COLOR,
					fontWeight: MUI_TABLE.HEADER.FONT_WEIGHT,
					fontSize: MUI_TABLE.HEADER.FONT_SIZE,
					letterSpacing: MUI_TABLE.HEADER.LETTER_SPACING,
					textTransform: "uppercase",
					padding: MUI_TABLE.HEADER.PADDING,
					borderBottom: "none",
					whiteSpace: "nowrap",
					lineHeight: 1.2,
					"&:not(:first-of-type)": {
						borderLeft: `1px solid ${MUI_TABLE.HEADER.DIVIDER}`,
					},
				},
				body: {
					padding: MUI_TABLE.BODY.PADDING,
					fontSize: "0.875rem",
					color: MUI_TABLE.BODY.COLOR,
					borderColor: MUI_TABLE.BORDER_COLOR,
				},
				footer: {
					padding: MUI_TABLE.BODY.PADDING,
					fontSize: "0.875rem",
					borderColor: MUI_TABLE.BORDER_COLOR,
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					"&:last-child td, &:last-child th": {
						borderBottom: 0,
					},
					"&.MuiTableRow-hover:hover": {
						backgroundColor: MUI_TABLE.ROW_HOVER,
					},
				},
			},
		},
	},
});

export function MUIThemeProvider({ children }: { children: ReactNode }) {
	return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
